<template>
  <div>
    <div class="columns">
      <div class="column is-one-fifth">
        <p class="has-text-weight-bold is-size-6 mb-sm">Missed Questions</p>
        <p class="has-text-grey is-size-7 mb-md">
          Questions suppliers asked that the assistant couldn't answer, ranked by
          how often they were asked. Turn one into a Q&amp;A, link it to an
          existing entry, or dismiss it.
        </p>
        <b-message v-if="optionsError" type="is-danger" size="is-small">
          The assistant groups &amp; categories could not be loaded, so questions
          can't be turned into Q&amp;As.
          <a href="#" @click.prevent="loadOptions">Retry</a>
        </b-message>
      </div>
      <div class="column">
        <!-- Toolbar -->
        <div class="has-background-white rounded-lg pa-md mb-xxs">
          <div class="columns is-vcentered is-mobile">
            <div class="column is-narrow">
              <div class="buttons has-addons is-marginless">
                <b-button
                  v-for="pill in statusPills"
                  :key="pill.value"
                  size="is-small"
                  rounded
                  :type="statusFilter === pill.value ? 'is-primary' : 'is-light'"
                  @click="setStatus(pill.value)">
                  {{ pill.label }}
                </b-button>
              </div>
            </div>
            <div class="column has-text-right is-size-7 has-text-grey">
              <span v-if="!loading">{{ total }} {{ total === 1 ? 'question' : 'questions' }}</span>
            </div>
            <div class="column is-narrow">
              <b-select v-model="orderBy" size="is-medium" expanded @input="sort">
                <option value="-ask_count">Most Asked</option>
                <option value="-last_asked_at">Recently Asked</option>
                <option value="last_asked_at">Least Recently Asked</option>
              </b-select>
            </div>
          </div>
        </div>
        <!-- Results -->
        <div class="has-background-white rounded-lg">
          <b-table :data="results" :mobile-cards="false" :loading="loading" hoverable class="rounded-lg is-size-7">
            <template>
              <!-- Question -->
              <b-table-column label="Question" v-slot="props">
                <p class="has-text-weight-bold">{{ props.row.question }}</p>
                <p v-if="props.row.user" class="has-text-grey-light">Last asked by {{ props.row.user.name }}</p>
              </b-table-column>
              <!-- Asked -->
              <b-table-column label="Asked" cell-class="nowrap" width="72" centered v-slot="props">
                <b-tag rounded>{{ props.row.ask_count }}x</b-tag>
              </b-table-column>
              <!-- Last Asked -->
              <b-table-column label="Last Asked" cell-class="nowrap" width="160" v-slot="props">
                {{ props.row.last_asked_at ? $dates.utc(props.row.last_asked_at).local().format('MMM D, YYYY') : '' }}
              </b-table-column>
              <!-- Status -->
              <b-table-column label="Status" cell-class="nowrap" width="104" centered v-slot="props">
                <b-tag rounded :type="statusType(props.row.status)">{{ statusLabel(props.row.status) }}</b-tag>
              </b-table-column>
              <!-- Linked Entry -->
              <b-table-column label="Linked Q&A" v-slot="props">
                <template v-if="props.row.entry">
                  <a href="#" class="has-text-info" @click.prevent="editEntry(props.row.entry)">{{ props.row.entry.title }}</a>
                  <b-tag v-if="props.row.entry.status === 'draft'" rounded type="is-warning" size="is-small" class="ml-xs">Draft</b-tag>
                </template>
                <span v-else class="has-text-grey-light">&mdash;</span>
              </b-table-column>
              <!-- Actions -->
              <b-table-column label="" cell-class="nowrap" width="256" v-slot="props">
                <div class="buttons is-right is-marginless" style="flex-wrap:nowrap">
                  <template v-if="props.row.status === 'new'">
                    <b-button size="is-small" type="is-primary" :disabled="updatingId !== null || !ready" @click="createEntry(props.row)">Create Q&amp;A</b-button>
                    <b-button size="is-small" :disabled="updatingId !== null" @click="linkEntry(props.row)">Link</b-button>
                    <b-button size="is-small" type="is-light" :loading="updatingId === props.row.id" :disabled="updatingId !== null" @click="dismiss(props.row)">Dismiss</b-button>
                  </template>
                  <template v-else>
                    <b-button size="is-small" type="is-light" :loading="updatingId === props.row.id" :disabled="updatingId !== null" @click="reopen(props.row)">Reopen</b-button>
                  </template>
                </div>
              </b-table-column>
            </template>
            <template #empty>
              <div class="py-xxl has-text-grey has-text-centered">
                {{ (loading) ? 'Loading data...' : 'No missed questions here — the assistant is keeping up!' }}
              </div>
            </template>
          </b-table>
          <div class="px-md pb-md">
            <b-pagination
              :total="total"
              :current.sync="currentPage"
              :rounded="true"
              :per-page="perPage"
              @change="paginate">
            </b-pagination>
          </div>
        </div>
      </div>
    </div>

    <!-- Create (prefilled) / Edit Q&A Modal -->
    <b-modal
      :active.sync="showEntryModal"
      has-modal-card
      :width="1100"
      :can-cancel="['escape', 'x']"
      @close="closeModals">
      <EntryModal
        v-if="showEntryModal"
        :entry="focusEntry"
        :prefill="prefill"
        :groups="groups"
        :categories="categories"
        @updated="onEntrySaved"
        @deleted="changed" />
    </b-modal>

    <!-- Link Entry Modal -->
    <b-modal
      :active.sync="showLinkModal"
      has-modal-card
      :can-cancel="['escape', 'x']"
      @close="closeModals">
      <LinkEntryModal
        v-if="showLinkModal"
        :question="focusQuestion"
        @updated="changed" />
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Filterable from '@/mixins/Filterable'
import EntryModal from './EntryModal'
import LinkEntryModal from './LinkEntryModal'

const STATUSES = {
  new: { label: 'New', type: 'is-info' },
  answered: { label: 'Answered', type: 'is-success' },
  dismissed: { label: 'Dismissed', type: 'is-light' }
}

export default {
  components: {
    EntryModal,
    LinkEntryModal
  },
  computed: {
    ...mapGetters(['api', 'assistantOptions']),
    groups () {
      return this.assistantOptions.groups || []
    },
    categories () {
      return this.assistantOptions.categories || []
    },
    ready () {
      return this.assistantOptions.groups !== null
    },
    optionsError () {
      return this.assistantOptions.error
    },
    endpoint () {
      return this.api.baseUrl + '/v1/assistant/missed-questions/'
    },
    // Mirror the applied status facet onto the quick pills.
    statusFilter () {
      const applied = (this.filters.facets && this.filters.facets.status) || []
      if (applied.length === 1) return applied[0].value
      return 'all'
    }
  },
  data () {
    return {
      key: 'settings.assistant-missed',
      orderBy: '-ask_count',
      perPage: 25,
      updatingId: null,
      showEntryModal: false,
      showLinkModal: false,
      // The missed question a modal is acting on, the entry being edited (when
      // opened from the Linked Q&A column), and the prefill for a new entry.
      focusQuestion: null,
      focusEntry: null,
      prefill: null,
      statusPills: [
        { value: 'new', label: 'New' },
        { value: 'answered', label: 'Answered' },
        { value: 'dismissed', label: 'Dismissed' },
        { value: 'all', label: 'All' }
      ]
    }
  },
  methods: {
    // Show new questions by default — that's the review queue.
    defaultFilters () {
      return {
        preset: 'custom',
        search: {},
        facets: {
          status: [{ label: 'New', value: 'new' }]
        },
        timestamps: {}
      }
    },
    statusLabel (status) {
      return STATUSES[status] ? STATUSES[status].label : status
    },
    statusType (status) {
      return STATUSES[status] ? STATUSES[status].type : ''
    },
    setStatus (value) {
      const filters = JSON.parse(JSON.stringify(this.filters))
      filters.facets = filters.facets || {}
      if (value === 'all') {
        delete filters.facets.status
      } else {
        const pill = this.statusPills.find(pill => pill.value === value)
        filters.facets.status = [{ label: pill.label, value: pill.value }]
      }
      filters.preset = 'custom'
      this.filter(filters)
    },
    // --- Row actions ---
    createEntry (row) {
      this.focusQuestion = row
      this.focusEntry = null
      this.prefill = { title: row.question }
      this.showEntryModal = true
    },
    editEntry (entry) {
      if (!this.ready) return
      this.focusQuestion = null
      this.focusEntry = entry
      this.prefill = null
      this.showEntryModal = true
    },
    linkEntry (row) {
      this.focusQuestion = row
      this.showLinkModal = true
    },
    dismiss (row) {
      this.update(row, { status: 'dismissed' }, 'Question dismissed.')
    },
    reopen (row) {
      this.update(row, { status: 'new', entry_id: null }, 'Question reopened.')
    },
    update (row, payload, message) {
      this.updatingId = row.id
      this.$http.patch(this.endpoint + row.id + '/', payload).then(() => {
        this.$buefy.toast.open({ type: 'is-success', message })
        this.load()
      }).catch(() => {
        this.$buefy.toast.open({ type: 'is-danger', message: 'The question could not be updated.' })
      }).finally(() => {
        this.updatingId = null
      })
    },
    // A Q&A was saved in the entry modal. If it was created from a missed
    // question, link it and mark the question answered.
    onEntrySaved (entry) {
      this.$store.dispatch('refreshAssistantConfig')
      if (this.focusQuestion === null) {
        this.load()
        return
      }
      this.$http.patch(this.endpoint + this.focusQuestion.id + '/', { entry_id: entry.id, status: 'answered' }).then(() => {
        this.$buefy.toast.open({ type: 'is-success', message: 'Q&A created and linked!' })
      }).catch(() => {
        this.$buefy.toast.open({ type: 'is-danger', message: 'The Q&A was created but could not be linked — use Link instead.' })
      }).finally(() => {
        this.load()
      })
    },
    changed () {
      this.$store.dispatch('refreshAssistantConfig')
      this.load()
    },
    closeModals () {
      this.focusQuestion = null
      this.focusEntry = null
      this.prefill = null
    },
    // Groups/categories feed the Create Q&A form; session-cached in the store
    // (shared with the Q&A page). Dispatching again after a failure retries.
    loadOptions () {
      return this.$store.dispatch('loadAssistantOptions')
    }
  },
  mixins: [Filterable],
  mounted () {
    this.loadOptions()
    this.load()
  }
}
</script>
